import type { GapGuideDebug } from './types';
import type { CandidateGeometryEntry, GeometryAudit } from './geometryAudit';
import type { VerticalAlignmentDebug, VerticalSpacingDebug } from './alignmentMath';
import type { MagneticSnapDebug } from './magneticSnap';

export const BUILDER_MOVEMENT_DEBUG_EVENT = 'eco3dcor:builder-movement-debug';

export function isMovementDebugRequested(search?: string) {
  if (typeof window === 'undefined' && search == null) return false;
  const params = new URLSearchParams(search ?? window.location.search);
  const queryValue = params.get('movementDebug');
  return queryValue === '1' || queryValue === 'true';
}

export type MovementDebugActiveGuide = {
  movingAnchor: string;
  targetBlockId?: string;
  targetAnchor: string;
  targetValue: number;
  movingValue: number;
  distancePx: number;
  snapDeltaPx: number;
};

export type MovementDebugGapGuide = {
  axis: 'x' | 'y';
  side: 'left' | 'right' | 'top' | 'bottom';
  neighbourBlockId?: string;
  gapPx: number;
  overlap?: boolean;
  displayLabel?: string;
  labelPosition: { x: number; y: number };
};

export type MovementDebugCandidateRejection = {
  blockId: string;
  reason:
    | 'large-container'
    | 'not-same-row'
    | 'contains-moving-box'
    | 'not-immediate-neighbour'
    | 'lower-priority-kind'
    | 'not-row-peer'
    | 'not-same-column'
    | 'not-column-peer';
};

export type MovementDebugAlignment = {
  movingBox: {
    left: number;
    right: number;
    top: number;
    bottom: number;
    centerX: number;
    centerY: number;
    width: number;
    height: number;
  };
  candidateCount: number;
  candidateRejections?: MovementDebugCandidateRejection[];
  activeXGuide?: MovementDebugActiveGuide;
  activeYGuide?: MovementDebugActiveGuide;
  gapGuides: MovementDebugGapGuide[];
  gapDebug: GapGuideDebug[];
  boxSource: 'content' | 'frame' | 'text-paragraph' | 'layout';
  coordinateSpace: 'section-layer-px';
  snapApplied?: boolean;
  rowPeerIds?: string[];
  explicitRowMode?: boolean;
  explicitColumnMode?: boolean;
  verticalAlignmentDebug?: VerticalAlignmentDebug;
  verticalSpacingDebug?: VerticalSpacingDebug;
};

export type { VerticalAlignmentDebug, VerticalSpacingDebug };

export type MovementDebugHoverGeometry = {
  blockId: string;
  hoverVisualBox?: Record<string, number>;
  hitTestBox?: Record<string, number>;
  selectedVisualBox?: Record<string, number>;
  chosenBoxSource: string;
  hoverUsesActiveVisualBox: boolean;
};

export type MovementDebugPayload = {
  source: 'drag' | 'keyboard' | 'resize' | 'hover';
  phase: 'start' | 'preview' | 'commit' | 'post-commit' | 'cancel' | 'visual';
  blockId?: string;
  blockIds?: string[];
  viewport?: 'desktop' | 'tablet' | 'mobile';
  handle?: string;
  input?: Record<string, unknown>;
  delta?: Record<string, unknown>;
  layout?: Record<string, unknown>;
  transform?: Record<string, unknown>;
  updates?: Array<Record<string, unknown>>;
  dom?: Record<string, unknown> | null;
  note?: string;
  movementBranch?: 'fine-style-offset' | 'grid-layout';
  fineOffset?: {
    succeeded?: boolean;
    reason?: string;
    before?: { offsetXPx?: number | null; offsetYPx?: number | null };
    after?: { offsetXPx?: number | null; offsetYPx?: number | null };
  };
  alignment?: MovementDebugAlignment;
  geometryAudit?: GeometryAudit;
  candidateGeometry?: CandidateGeometryEntry[];
  hoverGeometry?: MovementDebugHoverGeometry;
  magneticSnapDebug?: MagneticSnapDebug;
};

export type { MagneticSnapDebug };

function round(value: number) {
  return Math.round(value * 100) / 100;
}

export function getMovementDebugBlockSnapshot(blockId: string) {
  if (typeof document === 'undefined') return null;
  const roots = document.querySelectorAll<HTMLElement>('[data-builder-block-root="true"]');
  for (const root of Array.from(roots)) {
    if (root.getAttribute('data-builder-block-id') !== blockId) continue;
    const rect = root.getBoundingClientRect();
    return {
      left: round(rect.left),
      top: round(rect.top),
      width: round(rect.width),
      height: round(rect.height),
      transform: root.style.transform || null,
    };
  }
  return null;
}

export function emitMovementDebug(payload: MovementDebugPayload) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(BUILDER_MOVEMENT_DEBUG_EVENT, {
      detail: {
        ...payload,
        time: new Date().toLocaleTimeString(),
      },
    })
  );
}
